import { useState, type ReactElement } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button.js';
import { useAndroidAppStoreUrl, useCatalog } from '@/lib/i18n/catalog-context.js';
import { ANDROID_DISMISS_KEY, readAndroidProbe, shouldOfferAndroidApp } from '@/lib/android-app.js';
import { readIosStorage, rememberDismiss } from '@/lib/ios-app.js';

/**
 * Предложение установить приложение из Google Play.
 *
 * Разметка и её обоснования — те же, что у IosAppBanner.tsx (строка в потоке, обрезка
 * текста, цель нажатия крестика). Условия показа исключают друг друга по строке агента,
 * поэтому два баннера одновременно не появляются.
 *
 * Условия показа — web/src/lib/android-app.ts.
 */
export function AndroidAppBanner(): ReactElement | null {
  const c = useCatalog();
  const storeUrl = useAndroidAppStoreUrl();

  // Решение — один раз на монтирование, как у iPhone-баннера.
  const [open, setOpen] = useState(() =>
    shouldOfferAndroidApp({
      storeUrl,
      probe: readAndroidProbe(),
      storage: readIosStorage(),
      now: Date.now(),
    }),
  );

  if (!open) return null;

  const dismiss = (): void => {
    setOpen(false);
    rememberDismiss(readIosStorage(), Date.now(), ANDROID_DISMISS_KEY);
  };

  return (
    <aside
      data-banner="android-app"
      role="region"
      aria-label={c.common.androidApp.region}
      className="flex items-center gap-2 border-b border-mm-rule-warm bg-white px-2.5 py-2"
    >
      <img
        src="/apple-touch-icon.png"
        alt=""
        width={36}
        height={36}
        className="size-9 shrink-0 rounded-[9px]"
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-meta font-bold text-mm-ink">{c.common.androidApp.title}</p>
        <p className="truncate text-caption text-mm-text-subtle">{c.common.androidApp.text}</p>
      </div>

      <Button asChild size="sm" className="shrink-0">
        {/* Карточка Google Play: Chrome сам предложит открыть её в приложении магазина. */}
        <a href={storeUrl} target="_blank" rel="noopener noreferrer">
          {c.common.androidApp.install}
        </a>
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={c.common.androidApp.dismiss}
        onClick={dismiss}
        className="size-10 shrink-0 text-mm-text-subtle"
      >
        <X aria-hidden="true" />
      </Button>
    </aside>
  );
}
